import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Subtask } from "../models/Subtask";
import { SubtaskService } from "../services/SubtaskService";
import { useMatter } from "./MatterContext";

interface Errors {
  list?: Error | null;
  details?: Error | null;
  subsubtasks?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
  subsubtasks?: boolean;
}

interface SubtaskContextProps {
  subtasks: Subtask[];
  selectedSubtask: Subtask | null;
  setSelectedSubtask: (subtaskId: string) => void;
  createSubtask: (
    subtaskData: Omit<
      Subtask,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId"
    >,
  ) => Promise<string>;
  updateSubtask: (
    subtaskId: string,
    updatedData: Partial<Omit<Subtask, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteSubtaskById: (subtaskId: string) => void;
  loading: Loadings;
  error: Errors;
}

const SubtaskContext = createContext<SubtaskContextProps | undefined>(
  undefined,
);

export const useSubtask = (): SubtaskContextProps => {
  const context = useContext(SubtaskContext);
  if (!context) {
    throw new Error("useSubtask must be used within a SubtaskProvider");
  }
  return context;
};

export const SubtaskProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [selectedSubtask, setSelectedSubtask] = useState<Subtask | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  const setSelectedSubtaskById = async (subtaskId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const subtaskData = await SubtaskService.getSubtask(
        currentMatterId,
        subtaskId,
      );
      setSelectedSubtask(subtaskData);
      setLoadings((prev) => ({ ...prev, details: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createSubtask = async (
    subtaskData: Omit<
      Subtask,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ): Promise<string> => {
    const newSubtaskId = await SubtaskService.createSubtask(
      currentMatterId!,
      subtaskData,
    );
    return newSubtaskId;
  };

  const updateSubtask = async (
    subtaskId: string,
    updatedData: Partial<Omit<Subtask, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    if (!currentMatterId) return;
    await SubtaskService.updateSubtask(subtaskId, updatedData);
    await setSelectedSubtaskById(subtaskId);
  };

  const deleteSubtaskById = async (subtaskId: string) => {
    if (!currentMatterId) return;
    await SubtaskService.deleteSubtask(currentMatterId, subtaskId);
    setSubtasks((prev) => prev.filter((t) => t._id !== subtaskId));
    setSelectedSubtask(null);
  };

  return (
    <SubtaskContext.Provider
      value={{
        loading: loadings,
        error: errors,
        subtasks,
        selectedSubtask,
        createSubtask,
        updateSubtask,
        deleteSubtaskById,
        setSelectedSubtask: setSelectedSubtaskById,
      }}
    >
      {children}
    </SubtaskContext.Provider>
  );
};
